import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import Button from '~/components/buttons/NewButton';
import TextController from '~/components/controllers/TextController/TextController';
import ModalActions from '~/components/modals/Modal/ModalActions';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import IconSearch from '~/icons/icon-search.svg?react';
import { useSearchCompanyIdentity } from '~/services/company-identity';
import { CompanyIdentity } from '~/types/company-identity';
import { isItalianTaxNumber, isItalianVatNumber } from '~/utils/validation';
import styles from './AddCompanySearch.module.scss';

interface AddCompanySearchProps {
  initialValue: string;
  onNotFound: (taxNumber: string) => void;
  onFound: (taxNumber: string, identity: CompanyIdentity, available: boolean) => void;
}

const useCompanySearchSchema = () => {
  const { t } = useTranslation('validations');
  return useMemo(
    () =>
      z.object({
        taxNumber: z
          .string()
          .min(1)
          .superRefine((v, ctx) => {
            if (isItalianVatNumber(v) || isItalianTaxNumber(v)) {
              return;
            }

            if (new RegExp(/\d+/).test(v)) {
              return ctx.addIssue({
                message: t('exact_string', { count: 11 }),
                code: z.ZodIssueCode.custom,
              });
            }

            return ctx.addIssue({
              message: t('exact_string', { count: 16 }),
              code: z.ZodIssueCode.custom,
            });
          })
          .transform((x) => x.toUpperCase()),
      }),
    [t],
  );
};

type FormInput = z.input<ReturnType<typeof useCompanySearchSchema>>;
type FormOutput = z.output<ReturnType<typeof useCompanySearchSchema>>;

const AddCompanySearch: React.FC<AddCompanySearchProps> = ({ onFound, onNotFound, initialValue }) => {
  const { t } = useTranslation('auth', { keyPrefix: 'signup.add_company.search' });
  const { captureException } = useExceptionLogger();
  const companySearchSchema = useCompanySearchSchema();

  const form = useForm<FormInput, null, FormOutput>({
    defaultValues: {
      taxNumber: initialValue,
    },
    resolver: zodResolver(companySearchSchema),
  });

  const searchCompanyIdentity = useSearchCompanyIdentity();

  const identifyCompany = async (taxNumber: string) => {
    try {
      const { data, meta } = await searchCompanyIdentity.mutateAsync({ taxNumber, country: 'IT' });
      onFound(taxNumber, data, meta.available);
    } catch (error) {
      captureException(error, {
        extra: {
          tax_number: taxNumber,
        },
      });
      onNotFound(taxNumber);
    }
  };
  return (
    <form
      className={styles.root}
      noValidate
      onSubmit={form.handleSubmit((values) => {
        return identifyCompany(values.taxNumber);
      })}
    >
      <TextController
        autoFocus
        className={styles.searchText}
        control={form.control}
        name="taxNumber"
        label={t('label')}
        maxLength={16}
        required
      />

      <ModalActions>
        <Button className={styles.button} type="submit" loading={searchCompanyIdentity.isPending} Icon={IconSearch}>
          {t('button_label')}
        </Button>
      </ModalActions>
    </form>
  );
};

export default AddCompanySearch;
